import styled from "@emotion/styled";
import {
  Link,
  useRouteError,
  isRouteErrorResponse,
  useNavigate,
  useLocation,
} from "react-router";
import { Text, Button } from "@atlas-int/ui";
import { ArrowIcon } from "./landing/Utils";
import {
  fontWeight,
  borderRadius,
  status,
  surface,
  text,
  colorPrimitives,
} from "@atlas-int/tokens";
import type { ErrorLike } from "@apollo/client";
import {
  classifyError,
  getHumanReadableMessage,
  getRecoveryAction,
} from "../graphql/errors";

const ErrorContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 1rem;
  padding: 2rem 1rem;
`;

const ErrorBody = styled.p`
  max-width: 40rem;
  color: ${text.primary};
  margin: 0;
`;

const GoToButton = styled(Link)`
  background-color: ${surface.white};
  padding: 0.5rem 1.2rem 0.5rem 2rem;
  border-radius: ${borderRadius.pill};
  border: solid 1px ${colorPrimitives.blueSlate};
  color: ${colorPrimitives.blueSlate};
  text-decoration: none;
  font-weight: ${fontWeight.semibold};

  svg {
    width: 0.7rem;
    height: 0.7rem;
    margin-left: 0.7rem;
    fill: ${status.error};
  }

  &:hover {
    background-color: ${colorPrimitives.blueSlate};
    color: ${text.onDark};

    svg {
      fill: ${text.onDark};
    }
  }
`;

function asErrorLike(err: unknown): ErrorLike | null {
  if (err instanceof Error) return err as unknown as ErrorLike;
  if (err && typeof err === "object" && "message" in err) {
    return err as ErrorLike;
  }
  return null;
}

export default function ErrorPage() {
  const routeError = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();

  // react-router's own 404 (no matching route) — keep the existing copy
  if (isRouteErrorResponse(routeError) && routeError.status === 404) {
    return (
      <ErrorContainer id="error-page">
        <Text variant="heading-2">
          Uh-oh. The page you are looking for could not be found.
        </Text>
        <GoToButton to={"/"}>
          Go to the Atlas of Economic Complexity Home Page
          <ArrowIcon />
        </GoToButton>
      </ErrorContainer>
    );
  }

  const errorLike = asErrorLike(routeError);
  if (!errorLike) {
    return (
      <ErrorContainer id="error-page">
        <Text variant="heading-2">Something went wrong.</Text>
        <GoToButton to={"/"}>
          Go to the Atlas of Economic Complexity Home Page
          <ArrowIcon />
        </GoToButton>
      </ErrorContainer>
    );
  }

  const classified = classifyError(errorLike);
  const { title, message } = getHumanReadableMessage(classified);
  const recovery = getRecoveryAction(classified);

  const onClick =
    recovery.action === "reset-params"
      ? () => {
          // Country-profile routes carry the country ID in the path
          // (/countries/:country/:slide), so clearing just the query string
          // leaves the offending ID in place. Strip back to /countries so the
          // landing picks a random default.
          const target = location.pathname.startsWith("/countries/")
            ? "/countries"
            : location.pathname;
          navigate(target, { replace: true });
        }
      : () => navigate(0);

  return (
    <ErrorContainer id="error-page">
      <Text variant="heading-2">{title}</Text>
      <ErrorBody>{message}</ErrorBody>
      <Button variant="primary" size="lg" onClick={onClick}>
        {recovery.label}
      </Button>
    </ErrorContainer>
  );
}
